import React, { useState } from 'react';
import { apiJson } from '../apiClient';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import BarcodeScanField from '../components/BarcodeScanField';
import { categorySelectOptionsForUser } from '../constants/businessStockCategories';
import {
  factoryDefaultUnit,
  factoryQuantityHint,
  factoryShowsSellingPrice,
  isFactoryRawMaterialCategory,
} from '../constants/factoryStockCategories';
import { useFactoryRawMaterialCategories } from '../hooks/useFactoryRawMaterialCategories';
import {
  pharmacyCategoryRequiresExpiry,
  todayDateInputValue,
} from '../constants/pharmacyStockCategories';
import { isBrickFactoryUser, isBarPubUser, isPharmacyUser } from '../utils/businessType';
import { PRODUCT_UNITS, quantityHintForUnit } from '../constants/productUnits';
import PharmacyDrugNamePicker from '../components/PharmacyDrugNamePicker';
import './AddProductPage.css';

const AddProductPage = ({ onNavigate, rawMaterialOnly = false }) => {
  const { addProduct } = useProducts();
  const { user } = useAuth();
  const factoryMode = isBrickFactoryUser(user);
  const barMode = isBarPubUser(user);
  const pharmacyMode = isPharmacyUser(user);
  const { selectOptions: rawMaterialCategoryOptions } = useFactoryRawMaterialCategories(
    rawMaterialOnly && factoryMode
  );
  const PRODUCT_CATEGORIES = rawMaterialOnly ? rawMaterialCategoryOptions : categorySelectOptionsForUser(user);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [unit, setUnit] = useState(rawMaterialOnly ? 'bag' : 'pcs');
  const [quantity, setQuantity] = useState('');
  const [purchasingPrice, setPurchasingPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [lowStockLimit, setLowStockLimit] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [batchLot, setBatchLot] = useState('');
  const [barcode, setBarcode] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeScanHint, setBarcodeScanHint] = useState('');
  const [phase, setPhase] = useState('idle');
  const expiryRequired = pharmacyMode && pharmacyCategoryRequiresExpiry(category);
  const [error, setError] = useState('');

  const busy = phase === 'loading';
  const showSellingPrice = !factoryMode || factoryShowsSellingPrice(category);
  const factoryRawMaterial = rawMaterialOnly || (factoryMode && isFactoryRawMaterialCategory(category));

  const reset = () => {
    setName('');
    setCategory('');
    setUnit(rawMaterialOnly ? 'bag' : 'pcs');
    setQuantity('');
    setPurchasingPrice('');
    setSellingPrice('');
    setLowStockLimit('');
    setExpiryDate('');
    setBatchLot('');
    setBarcode('');
    setBarcodeScanHint('');
    setError('');
  };

  const handleBarcodeScan = async (code) => {
    setBarcode(code);
    if (!pharmacyMode) return;
    setBarcodeLoading(true);
    setError('');
    setBarcodeScanHint('');
    try {
      const data = await apiJson(`/pharmacy/products/barcode/${encodeURIComponent(code)}`);
      const p = data.product;
      if (p) {
        setName(p.name || '');
        if (p.category) setCategory(p.category);
        if (p.unit) setUnit(p.unit);
        if (p.sellingPrice != null && p.sellingPrice !== '') {
          setSellingPrice(String(p.sellingPrice));
        }
        setBarcodeScanHint(
          `Found "${p.name}" — enter quantity, expiry date, and batch/lot for this delivery.`
        );
      }
    } catch {
      setBarcodeScanHint('New barcode — choose or type the product name below.');
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!name.trim()) {
      setError('Enter a product name.');
      return;
    }
    if (!category) {
      setError('Select a category.');
      return;
    }
    const qty = factoryRawMaterial
      ? 0
      : (() => {
          const qtyRaw = String(quantity).trim();
          if (!qtyRaw) return null;
          return parseInt(qtyRaw, 10);
        })();
    if (!factoryRawMaterial) {
      if (qty === null) {
        setError('Enter quantity in stock.');
        return;
      }
      if (Number.isNaN(qty) || qty < 0) {
        setError('Enter a valid quantity (0 or more).');
        return;
      }
    }
    const buy = factoryRawMaterial
      ? 0
      : parseFloat(String(purchasingPrice).replace(',', '.'));
    let sell = 0;
    if (showSellingPrice && !factoryRawMaterial) {
      sell = parseFloat(String(sellingPrice).replace(',', '.'));
      if (Number.isNaN(sell) || sell < 0) {
        setError('Enter a valid selling price.');
        return;
      }
    }
    if (!factoryRawMaterial && (Number.isNaN(buy) || buy < 0)) {
      setError(factoryMode ? 'Enter a valid unit cost.' : 'Enter a valid purchasing price.');
      return;
    }
    const limitRaw = String(lowStockLimit).trim();
    const alertN = limitRaw === '' ? 0 : parseInt(limitRaw, 10);
    if (limitRaw !== '' && (Number.isNaN(alertN) || alertN < 0)) {
      setError('Enter a valid low-stock alert, or leave blank for no alert.');
      return;
    }
    if (pharmacyMode) {
      if (pharmacyCategoryRequiresExpiry(category) && !expiryDate.trim()) {
        setError('Expiry date is required for medicines in this category.');
        return;
      }
      if (expiryDate.trim() && expiryDate < todayDateInputValue()) {
        setError('Expiry date cannot be in the past when receiving stock.');
        return;
      }
      if (qty < 1) {
        setError('Enter quantity received (at least 1).');
        return;
      }
    }

    setPhase('loading');
    try {
      await addProduct(
        {
          name: name.trim(),
          category,
          quantity: qty,
          purchasingPrice: buy,
          sellingPrice: sell,
          lowStockLimit: alertN,
          unit: factoryRawMaterial ? unit : factoryMode ? 'pcs' : unit,
          expiryDate: pharmacyMode && expiryDate.trim() ? expiryDate.trim() : null,
          batchLot: pharmacyMode && batchLot.trim() ? batchLot.trim() : null,
          barcode: pharmacyMode && barcode.trim() ? barcode.trim() : null,
        },
        user
      );
      reset();
      if (typeof onNavigate === 'function') {
        onNavigate(rawMaterialOnly ? 'raw-materials-overview' : 'stock-list');
      }
    } catch (err) {
      setError(err?.message || 'Could not save product.');
    } finally {
      setPhase('idle');
    }
  };

  return (
    <div className="stock-add-page page-bleed">
      <header className="stock-add-header">
        <h1 className="stock-add-title">
          {rawMaterialOnly
            ? 'Add materials'
            : factoryMode
            ? 'Add plant stock item'
            : barMode
              ? 'Add drink / bar item'
              : pharmacyMode
                ? 'Receive medicine stock (batch)'
                : 'Add product'}
        </h1>
        <p className="stock-add-lede">
          {rawMaterialOnly
            ? 'Register a raw input for your factory catalog — name and type only. Purchase price goes on the material order; selling price when you receive delivery.'
            : factoryMode
            ? 'Register raw materials by name and category only — no quantity or prices here. Set purchase price when ordering; set selling price when receiving the order. Finished bricks and tiles can include quantity and prices on this form.'
            : barMode
              ? 'Add beverages and bar stock — beer, spirits, sodas, snacks — with unit (bottle, crate, litre) and bar selling price.'
              : pharmacyMode
                ? 'Receive a stock batch: category, quantity, expiry (required for medicines), batch/lot, and barcode for fast POS scanning.'
                : 'Create a stock item with unit (pcs, bag, metre…), costs, quantity, and low-stock alert.'}
        </p>
      </header>

      <div className="stock-add-card">
        <form className="stock-add-form" onSubmit={handleSubmit} noValidate>
          <div className="stock-add-grid">
            {pharmacyMode ? (
              <div className="stock-add-field stock-add-field--full stock-add-barcode-block">
                <BarcodeScanField
                  value={barcode}
                  onChange={(v) => {
                    setBarcode(v);
                    if (barcodeScanHint) setBarcodeScanHint('');
                  }}
                  onScan={handleBarcodeScan}
                  loading={barcodeLoading}
                  disabled={busy}
                  autoFocus
                  label="Scan barcode (receive stock)"
                  placeholder="Scan EAN / internal code — Enter to look up"
                  hint={barcodeScanHint || null}
                  hintTone={
                    !barcodeScanHint ? 'muted' : barcodeScanHint.startsWith('Found') ? 'ok' : 'warn'
                  }
                  inputClassName="stock-add-input"
                  buttonLabel="Look up"
                />
              </div>
            ) : null}
            {pharmacyMode ? (
              <PharmacyDrugNamePicker
                name={name}
                onNameChange={setName}
                category={category}
                onCategoryChange={setCategory}
                unit={unit}
                onUnitChange={setUnit}
                disabled={busy}
              />
            ) : (
              <label className="stock-add-field stock-add-field--full">
                <span className="stock-add-label">{factoryMode ? 'Item name' : 'Product name'}</span>
                <input
                  className="stock-add-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    factoryMode
                      ? 'e.g. Portland cement 50kg'
                      : barMode
                        ? 'e.g. Serengeti Lager 500ml'
                        : 'e.g. A4 ream 500 sheets'
                  }
                  required
                  disabled={busy}
                  autoComplete="off"
                />
              </label>
            )}

            <label className="stock-add-field stock-add-field--full">
              <span className="stock-add-label">Category</span>
              <select
                className="stock-add-input stock-add-select"
                value={category}
                onChange={(e) => {
                  const next = e.target.value;
                  setCategory(next);
                  if (factoryMode && !factoryShowsSellingPrice(next)) {
                    setSellingPrice('');
                  }
                  if (factoryRawMaterial || isFactoryRawMaterialCategory(next)) {
                    setUnit(factoryDefaultUnit(next));
                  }
                }}
                required
                disabled={busy}
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c.label + c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            {!factoryMode || factoryRawMaterial ? (
              <label className="stock-add-field">
                <span className="stock-add-label">
                  {factoryRawMaterial ? 'Unit (kipimo)' : 'Unit of measure'}
                </span>
                <select
                  className="stock-add-input stock-add-select"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  disabled={busy}
                >
                  {PRODUCT_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
                {factoryRawMaterial ? (
                  <span className="stock-add-hint">
                    How you count this material — bags (mifuko), tonnes, litres, kg, etc.
                  </span>
                ) : null}
              </label>
            ) : null}

            {!factoryRawMaterial ? (
              <label className="stock-add-field">
                <span className="stock-add-label">
                  {factoryMode ? 'Quantity on hand' : 'Quantity in stock'}
                </span>
                <input
                  className="stock-add-input"
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={factoryMode ? 'e.g. 120 bags' : 'e.g. 24'}
                  required
                  disabled={busy}
                />
                {factoryMode && category ? (
                  <span className="stock-add-hint">{factoryQuantityHint(category)}</span>
                ) : !factoryMode ? (
                  <span className="stock-add-hint">{quantityHintForUnit(unit)}</span>
                ) : null}
              </label>
            ) : (
              <p className="stock-add-field stock-add-field--full stock-add-hint" style={{ margin: 0 }}>
                Quantity and prices are set later: purchase price on the material order, selling price when the owner
                receives delivery. Stock on hand updates on receive.
                {category ? ` ${factoryQuantityHint(category)}` : ''}
              </p>
            )}

            <label className="stock-add-field">
              <span className="stock-add-label">Low stock alert</span>
              <input
                className="stock-add-input"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={lowStockLimit}
                onChange={(e) => setLowStockLimit(e.target.value)}
                placeholder="e.g. 5 — leave empty to turn off"
                disabled={busy}
              />
              <span className="stock-add-hint">
                Notify when quantity is at or below this number. Leave empty (or 0) for no alert.
              </span>
            </label>

            {pharmacyMode ? (
              <>
                <label className="stock-add-field">
                  <span className="stock-add-label">
                    Expiry date {expiryRequired ? '*' : '(optional)'}
                  </span>
                  <input
                    className="stock-add-input"
                    type="date"
                    value={expiryDate}
                    min={todayDateInputValue()}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    required={expiryRequired}
                    disabled={busy}
                  />
                  <span className="stock-add-hint">
                    Required for tablets, syrups, injections, etc. POS sells oldest batch first (FEFO).
                  </span>
                </label>
                <label className="stock-add-field">
                  <span className="stock-add-label">Batch / lot no. (recommended)</span>
                  <input
                    className="stock-add-input"
                    type="text"
                    value={batchLot}
                    onChange={(e) => setBatchLot(e.target.value)}
                    placeholder="e.g. LOT-2026-0412"
                    maxLength={64}
                    disabled={busy}
                    autoComplete="off"
                  />
                </label>
              </>
            ) : null}

            {!factoryRawMaterial ? (
              <label className="stock-add-field">
                <span className="stock-add-label">
                  {factoryMode ? 'Unit cost (TZS)' : 'Purchasing price (TZS)'}
                </span>
                <input
                  className="stock-add-input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={purchasingPrice}
                  onChange={(e) => setPurchasingPrice(e.target.value)}
                  placeholder="0.00"
                  required
                  disabled={busy}
                />
                {factoryMode && !showSellingPrice ? (
                  <span className="stock-add-hint">
                    What you pay suppliers per unit (bag, m³, etc.). Not sold directly — used in production.
                  </span>
                ) : null}
              </label>
            ) : null}

            {showSellingPrice ? (
              <label className="stock-add-field">
                <span className="stock-add-label">Selling price (TZS)</span>
                <input
                  className="stock-add-input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  placeholder="0.00"
                  required
                  disabled={busy}
                />
                {factoryMode ? (
                  <span className="stock-add-hint">Price when selling this finished item at POS or on delivery.</span>
                ) : null}
              </label>
            ) : null}
          </div>

          {error && (
            <div className="stock-add-alert stock-add-alert--error" role="alert">
              {error}
            </div>
          )}

          <div className="stock-add-actions">
            <button type="submit" className="stock-add-submit" disabled={busy}>
              {busy ? (
                <>
                  <span className="stock-add-spinner" aria-hidden />
                  Saving…
                </>
              ) : (
                factoryRawMaterial ? 'Save material' : factoryMode ? 'Save stock item' : 'Save product'
              )}
            </button>
            {typeof onNavigate === 'function' && (
              <button
                type="button"
                className="stock-add-secondary"
                onClick={() => onNavigate(rawMaterialOnly ? 'raw-materials-overview' : 'stock-list')}
                disabled={busy}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductPage;
